import { request } from './dashboardApi';

const billService = {
  fetchBills: async () => {
    try {
      const response = await request('/sales');
      if (response?.success) {
        let rawBills = [];
        if (Array.isArray(response.data)) rawBills = response.data;
        else if (response.data?.data && Array.isArray(response.data.data)) rawBills = response.data.data;
        else if (response.data?.bills && Array.isArray(response.data.bills)) rawBills = response.data.bills;

        return rawBills.map(b => billService._normalizeBill(b));
      }
      return [];
    } catch (error) {
      console.error('Error fetching bills:', error);
      return [];
    }
  },

  fetchSummary: async () => {
    try {
      const response = await request('/sales/summary');
      if (response?.success) {
        return response.data || {};
      }
      return {};
    } catch (error) {
      console.error('Error fetching bill summary:', error);
      return {};
    }
  },

  getBillDetails: async (id) => {
    try {
      const response = await request(`/sales/${id}`);
      if (response?.success && response.data) {
        return billService._normalizeBill(response.data);
      }
      return null;
    } catch (error) {
      console.error('Error fetching bill details:', error);
      return null;
    }
  },

  addBill: async (billData) => {
    try {
      const response = await request('/sales', {
        method: 'POST',
        body: JSON.stringify(billData)
      });
      if (response?.success) {
        await billService._updateStockLevels(billData);
        if (response.data) {
          response.data = billService._normalizeBill(response.data);
        }
        return response;
      }
      return response;
    } catch (error) {
      console.error('Error adding bill:', error);
      return { success: false, message: error.message };
    }
  },

  updateBill: async (id, billData) => {
    try {
      const originalBill = await billService.getBillDetails(id);
      const response = await request('/sales', {
        method: 'POST',
        body: JSON.stringify({ ...billData, id })
      });
      if (response?.success) {
        await billService._updateStockLevels(billData, originalBill);
        if (response.data) {
          response.data = billService._normalizeBill(response.data);
        }
        return response;
      }
      return response;
    } catch (error) {
      console.error('Error updating bill:', error);
      return { success: false, message: error.message };
    }
  },

  deleteBill: async (id) => {
    try {
      const billToDelete = await billService.getBillDetails(id);
      const response = await request(`/sales/${id}`, {
        method: 'DELETE'
      });
      if (response?.success) {
        if (billToDelete) {
          await billService._updateStockLevels(billToDelete, null, true);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting bill:', error);
      return false;
    }
  },

  uploadBillPhoto: async (file, billId) => {
    try {
      const formData = new FormData();
      formData.append('bill_id', billId);
      formData.append('photo', file);
      const response = await request('/sales/photo', {
        method: 'POST',
        body: formData
      });
      return response;
    } catch (error) {
      console.error('Error uploading photo:', error);
      return { success: false, message: error.message };
    }
  },

  _normalizeBill: (b) => {
    return {
      ...b,
      id: b.id?.toString(),
      uniqueKey: `bill-${b.id || Math.random()}`,
      transactionId: b.transaction_id,
      type: (b.bill_type || 'sale').toLowerCase(),
      billNumber: b.bill_number?.toString() || 'N/A',
      date: b.bill_date || b.created_at || new Date(),
      partyAddress: (() => {
        // Priority: Custom Bill Address > Bill specific address > Customer Profile Address
        const raw = b.party_address || b.bill_address || b.customer_address || b.address || '';
        if (typeof raw === 'string' && (raw.startsWith('{') || raw.startsWith('['))) {
          try { return JSON.parse(raw); } catch (e) { return raw; }
        }
        return raw;
      })(),
      customer: {
        id: b.customer_id || b.party_id || 0,
        address: (() => {
          const raw = b.party_address || b.bill_address || b.customer_address || b.address || '';
          if (typeof raw === 'string' && (raw.startsWith('{') || raw.startsWith('['))) {
            try { return JSON.parse(raw); } catch (e) { return raw; }
          }
          return raw;
        })(),
        customerName: b.customer_name || b.party_name || 'N/A',
        mobileNo: b.customer_mobile || b.party_mobile || '',
        gstin: b.customer_gstin || b.party_gstin || '',
        type: b.party_type || 'customer'
      },
      items: ((typeof (b.bill_items || b.items) === 'string')
        ? JSON.parse(b.bill_items || b.items)
        : (b.bill_items || b.items || [])).map(item => {
          const gstRateVal = (item.tax_rate ?? item.gst_rate ?? item.gstRate ?? item.taxRate ?? '0').toString();
          const gstRateClean = gstRateVal.replace('%', '').trim();
          const gstRatePercent = gstRateClean + '%';
          const taxInc = item.tax_included === 1 || item.tax_included === '1' || item.tax_included === true || item.taxIncluded === true || item.item?.taxIncluded === true;

          const pPrice = parseFloat(item.price || 0);
          const pUnitPrice = parseFloat(item.unit_price || item.unitPrice || 0);
          const pOriginalPrice = parseFloat(item.original_price || item.originalPrice || 0);
          const pSalePrice = parseFloat(item.sale_price || 0);
          const pPurchasePrice = parseFloat(item.purchase_price || 0);

          const finalPrice = pPrice || pUnitPrice || pOriginalPrice || 0;
          const finalOriginalPrice = pOriginalPrice || pUnitPrice || pPrice || pSalePrice || pPurchasePrice || 0;

          const qty = parseFloat(item.quantity || item.qty || item.item_qty || 1);
          const total = parseFloat(item.total || item.total_amount || 0);
          const tax = parseFloat(item.tax_amount || item.taxAmount || 0);
          const discVal = parseFloat(item.discount_value || item.discountValue || 0);
          const totalDisc = discVal * qty;
          const sub = total + totalDisc - tax;

          return {
            ...item,
            name: item.name || item.item_name || item.item?.name || 'N/A',
            quantity: qty,
            price: finalPrice,
            originalPrice: finalOriginalPrice,
            unitPrice: pUnitPrice || finalPrice,
            total: total,
            taxAmount: tax,
            discountValue: discVal,
            discountType: item.discount_type || item.discountType || 'rupee',
            totalDiscount: totalDisc, // Total discount for the row
            subtotal: sub, // Pre-discount pre-tax subtotal
            cgst: parseFloat(item.cgst || 0),
            sgst: parseFloat(item.sgst || 0),
            igst: parseFloat(item.igst || 0),
            taxIncluded: taxInc,
            gstRate: gstRatePercent,
            gst_rate: gstRateClean,
            item: item.item || {
              id: item.item_id || item.id,
              name: item.name || item.item_name || 'N/A',
              gstRate: gstRatePercent,
              gst_rate: gstRateClean,
              taxIncluded: taxInc,
              hsnSacCode: item.hsn_sac_code || item.hsn || ''
            }
          };
        }),
      isGst: b.is_gst === 1 || b.is_gst === '1' || b.is_gst === true || b.isGst === true,
      is_gst: b.is_gst === 1 || b.is_gst === '1' || b.is_gst === true || b.isGst === true ? 1 : 0,
      subtotal: parseFloat(b.subtotal || 0),
      totalDiscount: parseFloat(b.total_discount || 0),
      roundOffAmount: parseFloat(b.round_off_amount || b.round_off || 0),
      finalBillAmount: parseFloat(b.final_bill_amount || b.total_bill_amount || 0),
      paymentType: b.payment_mode || b.payment_type || 'unpaid',
      termsAndConditions: b.terms_n_conditions,
      privateNotes: b.private_note,
      expenseCategory: b.expense_category,
      addedToLedger: b.added_to_ledger === true || b.added_to_ledger === 1 || b.added_to_ledger === '1' || b.added_to_ledger === 'true',
      photoUrls: (b.photo_urls || b.photos || []).map(url => {
        if (typeof url === 'string') {
          let cleanUrl = url.replace('/api/v2/index.php/appstorage/', '/appstorage/');
          cleanUrl = cleanUrl.replace('/index.php/appstorage/', '/appstorage/');
          if (!cleanUrl.startsWith('http')) {
            cleanUrl = `https://khata.moxpal.com${cleanUrl.startsWith('/') ? '' : '/'}${cleanUrl}`;
          }
          return cleanUrl;
        }
        return url;
      })
    };
  },

  _updateStockLevels: async (bill, originalBill = null, isDeletion = false) => {
    try {
      const itemChanges = {};
      const billType = bill.bill_type || bill.type;

      const recordChange = (itemId, qty, type, reverse = false) => {
        if (!itemId) return;
        const change = type === 'sale' ? (reverse ? qty : -qty) : (reverse ? -qty : qty);
        itemChanges[itemId] = (itemChanges[itemId] || 0) + change;
      };

      if (originalBill) {
        originalBill.items.forEach(item => {
          recordChange(item.item_id || item.id, item.quantity, originalBill.type, true);
        });
      }

      if (isDeletion) {
        bill.items.forEach(item => {
          recordChange(item.item_id || item.id, item.quantity, billType, true);
        });
      } else {
        let items = bill.bill_items || bill.items;
        if (typeof items === 'string') {
          try { items = JSON.parse(items); } catch (e) { items = []; }
        }
        items.forEach(item => {
          recordChange(item.item_id || item.id, item.quantity, billType, false);
        });
      }

      for (const [itemId, change] of Object.entries(itemChanges)) {
        if (change === 0) continue;
        const itemRes = await request(`/items/${itemId}`);
        if (itemRes?.success && itemRes.data) {
          const currentStock = parseFloat(itemRes.data.opening_stock || 0);
          await request(`/items/${itemId}`, {
            method: 'PUT',
            body: JSON.stringify({ opening_stock: currentStock + change })
          });
        }
      }
    } catch (err) {
      console.error('Inventory Sync Failed:', err);
    }
  }
};

export default billService;
